import React, { useRef, useState, useEffect, useContext, useCallback } from "react";
import classes from "./CalendarPage.module.css";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useSmartRefreshStudioData } from '../../hooks/calendar/useSmartRefreshStudioData';

import ProfileMenu from "../../components/NavBar/ProfileMenu";
import CreatorNavBar from "../../components/NavBar/CreatorNavBar/CreatorNavBar";
import NavBarPhone from "../../components/NavBarPhone/NavBarPhone";
import Calendar from "../../components/CalendarPage/Calendar";
import Booking from "../../components/CalendarPage/Booking";
import ModesSwitch from "../../components/UI/ModesSwitch";
import { useStudioAvailabilities } from "../../hooks/calendar/useStudioAvailabilities";
import { useBlockedTimes } from "../../hooks/calendar/useBlockedTimes";
import { useHasStudios } from "../../hooks/useHasStudios";
import { useViewportHeight } from '../../hooks/useViewportHeight';
import useUnreadMessagesCount from '../../hooks/messages/useUnreadMessagesCount';
import useBookingNotifications from '../../hooks/useBookingNotifications';
import useSearchParamStudioId from '../../hooks/useSearchParamStudioId';
import { NavBarContext } from '../../App';
import { formatBerlinDate, getBerlinTodayForPicker } from '../../util/bookingDateTime';
import SaveNotification from "../../components/UI/SaveNotification/SaveNotification";
import { trackStudioUpdated } from "../../util/tracking";
import { isHelpMenuPanelTarget } from '../../util/isHelpMenuPanelTarget';

export const CalendarPage = () => {
  const { user } = useSelector((state) => state.auth);
  const { profilePicture, firstName, lastName, id: userID } = user;
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [selectedBookingDate, setSelectedBookingDate] = useState(null);
  const profileMenuRef = useRef(null);
  const userContainerRef = useRef(null);
  const [isSlideOut, setIsSlideOut] = useState(false);
  const bookingContainerRef = useRef(null);
  const overlayRef = useRef(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showOneDayBlockingModal, setShowOneDayBlockingModal] = useState(false);
  const [isDateClicked, setIsDateClicked] = useState(false);
  const [isFirstStudioChange, setIsFirstStudioChange] = useState(false);
  const [previousStudioId, setPreviousStudioId] = useState(null);
  const [showSaveNotification, setShowSaveNotification] = useState(false);
  const [saveNotificationMessage, setSaveNotificationMessage] = useState("Änderungen gespeichert");
  
  // Initialize viewport height fix for iOS Safari
  useViewportHeight();

  const { studioData, loading, error, refetch } = useSmartRefreshStudioData(userID, {
    pollInterval: 15000, // Poll every 15 seconds for faster updates
    enablePolling: true,
    refreshOnFocus: true,
    refreshOnVisibilityChange: true
  });

  const { selectedStudioId, setSelectedStudioId } = useSearchParamStudioId({
    studios: studioData,
  });

  useEffect(() => {
    const today = getBerlinTodayForPicker();
    const formattedDate = formatBerlinDate(today, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    
    setSelectedDate(today);
    setSelectedBookingDate({
      date: formattedDate,
      studioId: selectedStudioId
    });
  }, [selectedStudioId]);

  // Track studio changes for phone view behavior
  useEffect(() => {
    if (selectedStudioId && previousStudioId && selectedStudioId !== previousStudioId) {
      setIsFirstStudioChange(true);
      setIsDateClicked(false); // Hide booking on first studio change in phone view
      // Reset after a short delay to allow user to see the change
      setTimeout(() => {
        setIsFirstStudioChange(false);
      }, 100);
    }
    setPreviousStudioId(selectedStudioId);
  }, [selectedStudioId, previousStudioId]);

  const { hasStudios } = useHasStudios(userID);
  const unreadCount = useUnreadMessagesCount();
  const { activeView } = useContext(NavBarContext);
  const bookingNotifications = useBookingNotifications(activeView);
  const hasNotifications = (unreadCount > 0) || (bookingNotifications > 0);

  const { 
    availabilities
  } = useStudioAvailabilities(selectedStudioId);

  const {
    blockedTimes,
    loading: blockedTimesLoading,
    error: blockedTimesError,
    refetchBlockedTimes,
    createBlockedTime,
    deleteBlockedTime
  } = useBlockedTimes(selectedStudioId);

  const triggerCalendarToast = useCallback((message) => {
    setSaveNotificationMessage(message);
    setShowSaveNotification(true);
  }, []);

  const hideSaveNotification = useCallback(() => {
    setShowSaveNotification(false);
  }, []);

  const createBlockedTimeWithNotification = useCallback(
    async (data) => {
      const result = await createBlockedTime(data);
      trackStudioUpdated({
        studioId: data?.studioID || selectedStudioId,
        section: 'blocked_time_create',
        source: 'calendar'
      });
      triggerCalendarToast("Zeitspanne blockiert");
      return result;
    },
    [createBlockedTime, selectedStudioId, triggerCalendarToast]
  );

  const deleteBlockedTimeWithNotification = useCallback(
    async (item) => {
      const result = await deleteBlockedTime(item);
      trackStudioUpdated({
        studioId: item?.studioID || selectedStudioId,
        section: 'blocked_time_delete',
        source: 'calendar'
      });
      triggerCalendarToast("Blockierung entfernt");
      return result;
    },
    [deleteBlockedTime, selectedStudioId, triggerCalendarToast]
  );

  const refetchStudioDataWithNotification = useCallback(async () => {
    await refetch();
    triggerCalendarToast("Kalender aktualisiert");
  }, [refetch, triggerCalendarToast]);

  const handleCloseBooking = () => {
    setIsSlideOut(true);
    setSelectedDate(null);
    setIsDateClicked(false);
    setTimeout(() => {
      setSelectedBookingDate(null);
      setIsSlideOut(false);
    }, 300);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        overlayRef.current &&
        overlayRef.current.contains(event.target)
      ) {
        handleCloseBooking();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedBookingDate]);

  const hasBookings = selectedBookingDate && studioData?.some(studio => 
    studio.studioId === selectedBookingDate.studioId &&
    studio.bookings.some(booking => booking.date === selectedBookingDate.date)
  );

  const handleProfileMenuClick = () => {
    setShowProfileMenu(prev => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isHelpMenuPanelTarget(event.target)) {
        return;
      }

      if (
        profileMenuRef.current && 
        !profileMenuRef.current.contains(event.target) &&
        !userContainerRef.current?.contains(event.target)
      ) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);  

  const handleDateSelection = (date) => {
    setSelectedDate(date);
    // Don't show booking immediately if this is the first studio change
    if (!isFirstStudioChange) {
      setIsDateClicked(true);
    }
    
    if (date) {
      const formattedDate = `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
      setSelectedBookingDate({
        date: formattedDate,
        studioId: selectedStudioId
      });
    } else {
      // Handle the null case - perhaps reset the booking date
      setSelectedBookingDate(null);
    }
  };

  if (!loading && (!hasStudios || studioData.length === 0)) {
    const boxes = [];
    let boxCount = 0;
    for (let row = 0; row < 6; row++) {
      for (let col = 0; col < 7; col++) {
        if (row === 0 && col < 2) {
          boxes.push(<div key={`empty-${row}-${col}`} />);
        } else if (boxCount < 31) {
          boxes.push(
            <div
              key={`box-${boxCount}`}
              className={classes.noDataBox}
            />
          );
          boxCount++;
        } else {
          boxes.push(<div key={`empty-${row}-${col}`} />);
        }
      }
    }

    return (
      <div className={classes.noDataPage}>
        <header className={classes.noDataHeader}> 
          <svg xmlns="http://www.w3.org/2000/svg" width="53" height="18" viewBox="0 0 53 18" fill="none">
            <path d="M50.5002 9C51.8809 9 53.0002 7.88071 53.0002 6.5C53.0002 5.11929 51.8809 4 50.5002 4C49.1195 4 48.0002 5.11929 48.0002 6.5C48.0002 7.88071 49.1195 9 50.5002 9Z" fill="#FF003E"/>
            <path d="M9.93692 9.28445L8.58239 9.17069C5.23081 8.89942 4.10204 8.5494 4.10204 7.7881C4.10204 7.0268 5.52603 6.60677 8.46083 6.60677C11.5606 6.60677 12.8109 7.01805 13.0714 8.12062L13.1148 8.29563H16.5185L16.4838 8.05062C16.1452 5.14542 13.5577 3.73658 8.58239 3.73658C3.60712 3.73658 1.05437 5.11917 1.05437 7.83185C1.05437 10.317 3.09483 11.5158 8.11352 11.9709L9.32912 12.0846C12.6546 12.3734 13.8876 12.8109 13.8876 13.686C13.8876 14.6398 12.3681 15.1298 9.36385 15.1298C5.96017 15.1298 4.2236 14.491 4.05863 13.1697L4.03258 12.9772H0.628906V13.2135C0.785197 16.3899 3.63317 18 9.10336 18C14.5736 18 16.944 16.4949 16.944 13.5285C16.944 10.8858 14.9817 9.69572 9.93692 9.28445Z" fill="#1F1F1F"/>
            <path d="M42.1415 4.17403V9.8444C42.1415 13.0209 40.0663 14.9897 36.7321 14.9897C33.5976 14.9897 32.3733 13.5809 32.3733 9.95816V4.17403H21.8497V0L18.7152 1.33009V13.2834C18.7152 16.5823 20.2954 17.9912 24.003 17.9912C25.2968 17.9912 26.5384 17.7724 27.589 17.5537L27.7627 17.5187V14.7097L27.5022 14.7622C26.139 15.0422 25.5312 15.1473 24.5587 15.1473C22.7614 15.1473 21.8497 14.7185 21.8497 12.3646V6.7992H30.793L29.1433 8.05928V10.982C29.1433 15.7685 31.1577 17.9912 35.4731 17.9912C38.2603 17.9912 40.5525 17.0199 42.1415 15.1648V17.5537H45.3715V4.17403H42.1415Z" fill="#1F1F1F"/>
          </svg>
        </header>
        <header className={classes.header}>
            <Link to="/profile" className={classes.logo}>
                <svg xmlns="http://www.w3.org/2000/svg" width="52" height="18" viewBox="0 0 52 18" fill="none">
                    <g clipPath="url(#clip0_8485_11701)">
                    <path d="M9.24209 9.11699L7.89716 9.00528C4.56932 8.73889 3.44854 8.39516 3.44854 7.64756C3.44854 6.89996 4.86244 6.48749 7.77646 6.48749C10.8543 6.48749 12.0958 6.89137 12.3544 7.9741L12.3975 8.14596H15.7771L15.7426 7.90536C15.4064 5.05244 12.8372 3.66895 7.89716 3.66895C2.95712 3.66895 0.422446 5.02666 0.422446 7.69053C0.422446 10.131 2.44846 11.3082 7.4316 11.7551L8.63859 11.8668C11.9406 12.1504 13.1648 12.58 13.1648 13.4393C13.1648 14.376 11.6561 14.8572 8.67308 14.8572C5.29351 14.8572 3.56924 14.2299 3.40543 12.9323L3.37957 12.7433H0V12.9753C0.155184 16.0946 2.98299 17.6757 8.41444 17.6757C13.8459 17.6757 16.1995 16.1977 16.1995 13.2847C16.1995 10.6895 14.2511 9.52086 9.24209 9.11699Z" fill="black"/>
                    <path d="M41.2191 4.09892V9.66726C41.2191 12.7866 39.1586 14.72 35.848 14.72C32.7357 14.72 31.5201 13.3365 31.5201 9.77898V4.09892H21.071V0L17.9587 1.30615V13.0444C17.9587 16.284 19.5278 17.6675 23.2091 17.6675C24.4937 17.6675 25.7266 17.4526 26.7698 17.2378L26.9422 17.2034V14.445L26.6835 14.4966C25.33 14.7716 24.7265 14.8747 23.7609 14.8747C21.9763 14.8747 21.071 14.4536 21.071 12.1421V6.67686H29.951L28.313 7.91427V10.7844C28.313 15.4848 30.3131 17.6675 34.5979 17.6675C37.3654 17.6675 39.6414 16.7136 41.2191 14.8919V17.2378H44.4263V4.09892H41.2191Z" fill="black"/>
                    <path d="M48.9173 7.80391C50.0817 7.80391 51.0257 6.90461 51.0257 5.79526C51.0257 4.68592 50.0817 3.78662 48.9173 3.78662C47.7528 3.78662 46.8088 4.68592 46.8088 5.79526C46.8088 6.90461 47.7528 7.80391 48.9173 7.80391Z" fill="#DF315A"/>
                    </g>
                    <defs>
                        <clipPath id="clip0_8485_11701">
                            <rect width="51.0642" height="17.6761" fill="white"/>
                        </clipPath>
                    </defs>
                </svg>
            </Link>
            <Link to="/" className={classes.navigationLinks}>
                <svg xmlns="http://www.w3.org/2000/svg" width="63" height="22" viewBox="0 0 63 22" fill="none">
                    <path d="M59.881 10.9998C61.5389 10.9998 62.8828 9.65582 62.8828 7.99799C62.8828 6.34015 61.5389 4.99621 59.881 4.99621C58.2232 4.99621 56.8792 6.34015 56.8792 7.99799C56.8792 9.65582 58.2232 10.9998 59.881 10.9998Z" fill="#DF315A"/>
                    <path d="M11.1762 11.3413L9.54985 11.2047C5.52557 10.879 4.17024 10.4587 4.17024 9.54462C4.17024 8.63052 5.88004 8.12619 9.40389 8.12619C13.1258 8.12619 14.6271 8.62001 14.9399 9.94389L14.992 10.154H19.0788L19.0371 9.85983C18.6305 6.37153 15.5237 4.67992 9.54985 4.67992C3.57598 4.67992 0.510854 6.34001 0.510854 9.59716C0.510854 12.5811 2.96087 14.0206 8.98687 14.5669L10.4464 14.7035C14.4395 15.0503 15.9199 15.5756 15.9199 16.6263C15.9199 17.7715 14.0954 18.3599 10.4882 18.3599C6.40132 18.3599 4.3162 17.5929 4.11811 16.0064L4.08683 15.7752H0V16.0589C0.187661 19.8729 3.60726 21.8062 10.1754 21.8062C16.7435 21.8062 19.5897 19.999 19.5897 16.4372C19.5897 13.2641 17.2335 11.8351 11.1762 11.3413Z" fill="white"/>
                    <path d="M49.8447 5.20516V12.0136C49.8447 15.8277 47.3529 18.1917 43.3495 18.1917C39.5859 18.1917 38.1159 16.5001 38.1159 12.1502V5.20516H25.48V0.193359L21.7164 1.79041V16.1429C21.7164 20.104 23.6138 21.7956 28.0656 21.7956C29.619 21.7956 31.1098 21.5329 32.3713 21.2703L32.5799 21.2282V17.8555L32.2671 17.9185C30.6303 18.2548 29.9005 18.3808 28.7328 18.3808C26.5747 18.3808 25.48 17.866 25.48 15.0396V8.35724H36.2184L34.2375 9.87024V13.3795C34.2375 19.1268 36.6563 21.7956 41.8378 21.7956C45.1844 21.7956 47.9368 20.6293 49.8447 18.4019V21.2703H53.723V5.20516H49.8447Z" fill="white"/>
                </svg>
            </Link>
            <CreatorNavBar />
            <div className={classes.headerContent}>
                <ModesSwitch />
                <div className={classes.userWrapper}>
                    {hasNotifications && (
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="13" 
                        height="13" 
                        viewBox="0 0 13 13" 
                        fill="none"
                        style={{
                          position: 'absolute',
                          top: '0',
                          right: '0',
                          zIndex: 10
                        }}
                      >
                        <path 
                          d="M6.5 0.75C9.67573 0.75 12.25 3.32438 12.25 6.5C12.25 9.6756 9.67571 12.25 6.5 12.25C3.32448 12.25 0.750019 9.67561 0.75 6.5C0.75 3.32436 3.32446 0.75 6.5 0.75Z" 
                          fill="var(--Stu-Rot, #DF315A)" 
                          stroke="var(--Black, #000)" 
                          strokeWidth="1.5"
                        />
                      </svg>
                    )}
                    <div 
                        ref={userContainerRef} 
                        onClick={handleProfileMenuClick} 
                        className={classes.userContainer}
                        style={{ position: 'relative' }}
                      >
                        <div className={classes.userImageContainer}>
                          <img src={profilePicture} alt="User Profile" /> 
                        </div>
                    </div>
                </div>
            </div>
        </header>
        <h1 className={`${classes.noDataTitle} titleSemibold2`}>Kalender</h1>
        <div className={classes.noDataContainerWrapper}>
        <div className={classes.noDataContainer}>
          <div className={classes.noDataGrid}>
            {boxes}
          </div>
          <div className={classes.noDataFooter}>
            <h2 className={classes.noDataFooterTitle}>Erstelle dein Studio</h2>
            <p className={`${classes.noDataFooterText} mediumLarge`}>
              In wenigen Klicks Studio erstellen<br />und gebucht werden
            </p>
            <Link to="/studio-erstellen" className={classes.createStudioButton}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path fillRule="evenodd" clipRule="evenodd" d="M17 9.5317H1V8.45539H17V9.5317Z" fill="white"/>
                  <path fillRule="evenodd" clipRule="evenodd" d="M9.53433 1V17H8.45801V1H9.53433Z" fill="white"/>
                  <path fillRule="evenodd" clipRule="evenodd" d="M17 9.5317H1V8.45539H17V9.5317Z" stroke="white" strokeWidth="0.2" strokeMiterlimit="10"/>
                  <path fillRule="evenodd" clipRule="evenodd" d="M9.53433 1V17H8.45801V1H9.53433Z" stroke="white" strokeWidth="0.2" strokeMiterlimit="10"/>
                </svg>
                Studio erstellen
            </Link>
          </div>
        </div>  
      </div>
      <div className={classes.mobileNavBar}>
        <NavBarPhone fromCalendar={true} />
      </div>
      </div>
    );
  }

  return (
      <div className={classes.calendarPage}>
        <header className={classes.header}>
            <Link to="/profile" className={classes.logo}>
                <svg xmlns="http://www.w3.org/2000/svg" width="52" height="18" viewBox="0 0 52 18" fill="none">
                    <g clipPath="url(#clip0_8485_11701)">
                    <path d="M9.24209 9.11699L7.89716 9.00528C4.56932 8.73889 3.44854 8.39516 3.44854 7.64756C3.44854 6.89996 4.86244 6.48749 7.77646 6.48749C10.8543 6.48749 12.0958 6.89137 12.3544 7.9741L12.3975 8.14596H15.7771L15.7426 7.90536C15.4064 5.05244 12.8372 3.66895 7.89716 3.66895C2.95712 3.66895 0.422446 5.02666 0.422446 7.69053C0.422446 10.131 2.44846 11.3082 7.4316 11.7551L8.63859 11.8668C11.9406 12.1504 13.1648 12.58 13.1648 13.4393C13.1648 14.376 11.6561 14.8572 8.67308 14.8572C5.29351 14.8572 3.56924 14.2299 3.40543 12.9323L3.37957 12.7433H0V12.9753C0.155184 16.0946 2.98299 17.6757 8.41444 17.6757C13.8459 17.6757 16.1995 16.1977 16.1995 13.2847C16.1995 10.6895 14.2511 9.52086 9.24209 9.11699Z" fill="black"/>
                    <path d="M41.2191 4.09892V9.66726C41.2191 12.7866 39.1586 14.72 35.848 14.72C32.7357 14.72 31.5201 13.3365 31.5201 9.77898V4.09892H21.071V0L17.9587 1.30615V13.0444C17.9587 16.284 19.5278 17.6675 23.2091 17.6675C24.4937 17.6675 25.7266 17.4526 26.7698 17.2378L26.9422 17.2034V14.445L26.6835 14.4966C25.33 14.7716 24.7265 14.8747 23.7609 14.8747C21.9763 14.8747 21.071 14.4536 21.071 12.1421V6.67686H29.951L28.313 7.91427V10.7844C28.313 15.4848 30.3131 17.6675 34.5979 17.6675C37.3654 17.6675 39.6414 16.7136 41.2191 14.8919V17.2378H44.4263V4.09892H41.2191Z" fill="black"/>
                    <path d="M48.9173 7.80391C50.0817 7.80391 51.0257 6.90461 51.0257 5.79526C51.0257 4.68592 50.0817 3.78662 48.9173 3.78662C47.7528 3.78662 46.8088 4.68592 46.8088 5.79526C46.8088 6.90461 47.7528 7.80391 48.9173 7.80391Z" fill="#DF315A"/>
                    </g>
                    <defs>
                        <clipPath id="clip0_8485_11701">
                            <rect width="51.0642" height="17.6761" fill="white"/>
                        </clipPath>
                    </defs>
                </svg>
            </Link>
            <Link to="/" className={classes.navigationLinks}>
                <svg xmlns="http://www.w3.org/2000/svg" width="63" height="22" viewBox="0 0 63 22" fill="none">
                    <path d="M59.881 10.9998C61.5389 10.9998 62.8828 9.65582 62.8828 7.99799C62.8828 6.34015 61.5389 4.99621 59.881 4.99621C58.2232 4.99621 56.8792 6.34015 56.8792 7.99799C56.8792 9.65582 58.2232 10.9998 59.881 10.9998Z" fill="#DF315A"/>
                    <path d="M11.1762 11.3413L9.54985 11.2047C5.52557 10.879 4.17024 10.4587 4.17024 9.54462C4.17024 8.63052 5.88004 8.12619 9.40389 8.12619C13.1258 8.12619 14.6271 8.62001 14.9399 9.94389L14.992 10.154H19.0788L19.0371 9.85983C18.6305 6.37153 15.5237 4.67992 9.54985 4.67992C3.57598 4.67992 0.510854 6.34001 0.510854 9.59716C0.510854 12.5811 2.96087 14.0206 8.98687 14.5669L10.4464 14.7035C14.4395 15.0503 15.9199 15.5756 15.9199 16.6263C15.9199 17.7715 14.0954 18.3599 10.4882 18.3599C6.40132 18.3599 4.3162 17.5929 4.11811 16.0064L4.08683 15.7752H0V16.0589C0.187661 19.8729 3.60726 21.8062 10.1754 21.8062C16.7435 21.8062 19.5897 19.999 19.5897 16.4372C19.5897 13.2641 17.2335 11.8351 11.1762 11.3413Z" fill="white"/>
                    <path d="M49.8447 5.20516V12.0136C49.8447 15.8277 47.3529 18.1917 43.3495 18.1917C39.5859 18.1917 38.1159 16.5001 38.1159 12.1502V5.20516H25.48V0.193359L21.7164 1.79041V16.1429C21.7164 20.104 23.6138 21.7956 28.0656 21.7956C29.619 21.7956 31.1098 21.5329 32.3713 21.2703L32.5799 21.2282V17.8555L32.2671 17.9185C30.6303 18.2548 29.9005 18.3808 28.7328 18.3808C26.5747 18.3808 25.48 17.866 25.48 15.0396V8.35724H36.2184L34.2375 9.87024V13.3795C34.2375 19.1268 36.6563 21.7956 41.8378 21.7956C45.1844 21.7956 47.9368 20.6293 49.8447 18.4019V21.2703H53.723V5.20516H49.8447Z" fill="white"/>
                </svg>
            </Link>
            <CreatorNavBar />
            <div className={classes.headerContent}>
                <ModesSwitch />
                <div className={classes.userWrapper}>
                    {hasNotifications && (
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="13" 
                        height="13" 
                        viewBox="0 0 13 13" 
                        fill="none"
                        style={{
                          position: 'absolute',
                          top: '0',
                          right: '0',
                          zIndex: 10
                        }}
                      >
                        <path 
                          d="M6.5 0.75C9.67573 0.75 12.25 3.32438 12.25 6.5C12.25 9.6756 9.67571 12.25 6.5 12.25C3.32448 12.25 0.750019 9.67561 0.75 6.5C0.75 3.32436 3.32446 0.75 6.5 0.75Z" 
                          fill="var(--Stu-Rot, #DF315A)" 
                          stroke="var(--Black, #000)" 
                          strokeWidth="1.5"
                        />
                      </svg>
                    )}
                    <div 
                        ref={userContainerRef} 
                        onClick={handleProfileMenuClick} 
                        className={classes.userContainer}
                        style={{ position: 'relative' }}
                      >
                        <div className={classes.userImageContainer}>
                          <img src={profilePicture} alt="User Profile" /> 
                        </div>
                    </div>
                </div>
            </div>
        </header>
        <div className={classes.contentContainer}>
          <Calendar 
            // hasStudios={hasStudios}
            availabilities={availabilities}
            showOneDayBlockingModal={showOneDayBlockingModal}
            setShowOneDayBlockingModal={setShowOneDayBlockingModal}
            selectedStudioId={selectedStudioId} 
            setSelectedStudioId={setSelectedStudioId}
            selectedDate={selectedDate} 
            setSelectedDate={handleDateSelection}
            setSelectedBookingDate={setSelectedBookingDate} 
            data={studioData} 
            isLoading={loading} 
            error={error}
            blockedTimes={blockedTimes}
            blockedTimesLoading={blockedTimesLoading}
            blockedTimesError={blockedTimesError}
            refetchBlockedTimes={refetchBlockedTimes}
            createBlockedTime={createBlockedTimeWithNotification}
            deleteBlockedTime={deleteBlockedTimeWithNotification}
            onCalendarSuccess={triggerCalendarToast}
          />
          <div className={classes.bookingContainer}>
            <Booking 
              hasStudios={hasStudios}
              availabilities={availabilities}
              showOneDayBlockingModal={showOneDayBlockingModal}
              setShowOneDayBlockingModal={setShowOneDayBlockingModal}
              selectedStudioId={selectedStudioId}
              selectedBookingDate={selectedBookingDate} 
              data={studioData} 
              isLoading={loading} 
              error={error} 
              onClose={handleCloseBooking}
              onBookingUpdate={refetchStudioDataWithNotification}
              blockedTimes={blockedTimes}
              blockedTimesError={blockedTimesError}
              createBlockedTime={createBlockedTimeWithNotification}
              deleteBlockedTime={deleteBlockedTimeWithNotification}
            />
          </div>
          <div 
            ref={bookingContainerRef}
            className={`${classes.bookingContainerPhone} ${!selectedBookingDate ? classes.dateClicked : ''}`}
            style={{
              transform: !isDateClicked ? 'translateY(100%)' : 
                        isSlideOut ? 'translateY(100%)' : 'translateY(0)',
              transition: 'transform 0.3s ease-in-out'
            }}
          >
            <Booking 
              hasStudios={hasStudios}
              selectedBookingDate={selectedBookingDate} 
              data={studioData} 
              isLoading={loading} 
              error={error}
              onClose={handleCloseBooking}
              availabilities={availabilities}
              showOneDayBlockingModal={showOneDayBlockingModal}
              setShowOneDayBlockingModal={setShowOneDayBlockingModal}
              selectedStudioId={selectedStudioId}
              onBookingUpdate={refetchStudioDataWithNotification}
              blockedTimes={blockedTimes}
              blockedTimesError={blockedTimesError}
              createBlockedTime={createBlockedTimeWithNotification}
              deleteBlockedTime={deleteBlockedTimeWithNotification}
            />
        </div>
        </div>
        <div className={classes.mobileNavBar}>
            <NavBarPhone fromCalendar={true} />
        </div>
        {isDateClicked && <div className={`${classes.overlay} ${!selectedBookingDate ? classes.dateClicked : ''}`} ref={overlayRef} />}
        {showProfileMenu && (
          <ProfileMenu
            ref={profileMenuRef}
            profilePicture={profilePicture}
            firstName={firstName}
            lastName={lastName}
            setShowProfileMenu={setShowProfileMenu}
            // fromProfile={fromProfile}
          />
        )}
        <SaveNotification
          show={showSaveNotification}
          onHide={hideSaveNotification}
          message={saveNotificationMessage}
        />
      </div>
  );
};

export default CalendarPage;
